import { HttpErrorResponse } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { UserProfile } from '@models/webapi/user-profile';
import { FULL_ACCESS_DENIED_ROUTE, FULL_CONNECTION_PROBLEM_ROUTE, FULL_LOGOUT_ROUTE } from '@routes';
import { CompanyContextService, UserService } from '@services';
import { BehaviorSubject, catchError, filter, Observable, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EytcAuthService {
  private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private authService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);

  readonly #router = inject(Router);
  readonly #destroyRef: DestroyRef = inject(DestroyRef);
  readonly #userService = inject(UserService);
  readonly #isUserLogged$ = new BehaviorSubject<boolean>(false);
  readonly #companyContextService = inject(CompanyContextService);

  public readonly userProfile = signal<UserProfile | null>(null);
  public readonly userRoles = computed(() => {
    const currentCompany = this.#companyContextService.currentCompany();
    const userProfile = this.userProfile();

    if (currentCompany && userProfile) {
      const currentUserCompany = userProfile.companies.find(company => company.id === currentCompany.id);
      return currentUserCompany?.roles ?? [];
    }

    return [];
  });

  public getUserLoginState$(): Observable<boolean> {
    return this.#isUserLogged$.asObservable();
  }

  public get azureActiveDirectoryUserEmail(): string | null {
    return (
      (this.authService.instance.getActiveAccount()?.idTokenClaims as { email?: string } | undefined)?.email ?? null
    );
  }

  public get azureActiveDirectoryUserPrincipalName(): string | null {
    return this.authService.instance.getActiveAccount()?.username ?? null;
  }

  public init(): void {
    this.authService.handleRedirectObservable().subscribe();
    this.authService.instance.enableAccountStorageEvents();
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        this.trySetActiveAccount();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
        this.tryLoadUserProfile();
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        this.trySetActiveAccount();
      });

    this.#isUserLogged$
      .pipe(
        filter(x => x),
        switchMap(() => this.#userService.getProfile()),
        tap(userProfile => {
          this.userProfile.set(userProfile);
        }),
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse) {
            if (error.status === 401 || error.status === 403) {
              void this.#router.navigate([FULL_ACCESS_DENIED_ROUTE], {
                skipLocationChange: true,
                state: {
                  checkUser: false,
                  sourceUrl: window.location.href,
                },
              });
            } else {
              void this.#router.navigate([FULL_CONNECTION_PROBLEM_ROUTE], {
                skipLocationChange: true,
              });
            }
          }

          throw error;
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();

    this.trySetActiveAccount();
  }

  public login(): void {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  public logout(automaticLogout: boolean): void {
    const accountInfo = this.authService.instance.getActiveAccount();

    this.authService.logout({
      account: accountInfo, // needed for promptless logout
      postLogoutRedirectUri: !automaticLogout ? FULL_LOGOUT_ROUTE : `${FULL_LOGOUT_ROUTE}?sessionTimeout`,
    });
  }

  public isUserLogged(): boolean {
    return !!this.authService.instance.getActiveAccount();
  }

  public get isUserLogged$(): Observable<boolean> {
    return this.#isUserLogged$.asObservable();
  }

  private trySetActiveAccount(): void {
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }

    this.tryLoadUserProfile();
  }

  private tryLoadUserProfile(): void {
    if (this.isUserLogged()) {
      this.#isUserLogged$.next(true);
    }
  }
}
